"use client"

import { useEffect, useRef } from "react"
import { usePathname, useSearchParams } from "next/navigation"

import { removeEmptyTrackingValues } from "@/lib/ga4TrackingHelper"
import { sanitizeAnalyticsPath, sanitizeAnalyticsUrl } from "@/lib/tracking/url-sanitizer"

declare global {
  interface Window {
    dataLayer?: Object[]
  }
}

function getCurrentPagePath(pathname: string | null, searchParams: URLSearchParams | null) {
  const path = pathname || window.location.pathname
  const query = searchParams?.toString() || window.location.search.replace(/^\?/, "")
  const hash = window.location.hash

  return `${path}${query ? `?${query}` : ""}${hash}`
}

function getCurrentPageLocation(pathname: string | null, searchParams: URLSearchParams | null) {
  return `${window.location.origin}${getCurrentPagePath(pathname, searchParams)}`
}

export default function VirtualPageViewTrackingClient() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const initialRenderRef = useRef(true)

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false
      return
    }

    const frameId = window.requestAnimationFrame(() => {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(
        removeEmptyTrackingValues({
          event: "virtual_page_view",
          page_location: sanitizeAnalyticsUrl(getCurrentPageLocation(pathname, searchParams)),
          page_path: sanitizeAnalyticsPath(getCurrentPagePath(pathname, searchParams)),
          page_title: document.title,
        })
      )
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [pathname, searchParams])

  return null
}
